/** @format */

import { Profile, Configuration } from '../../models/profile';
import { StatHoliday } from 'src/app/services';
import { TimeBank } from 'src/app/models/timeBank';
import { EmailNotification } from 'src/app/models/notifications';
import { Observable } from 'rxjs';
import { SafeUrl } from '@angular/platform-browser';

export interface Timesheet {
	id: number;
	startDate: Date;
	endDate: Date;
	payPeriod: string;
	payPeriodIndex: number;
	payDate: Date;
	payPeriodStartDate: Date;
	status: string;
	metadata: TimesheetMetadata[];
	entries: TimesheetEntry2[];
	savedDateTime?: Date;
	submittedDateTime?: Date;
	approvedDateTime?: Date;
	sendToJDEDateTime?: Date;
	rolledBackDateTime?: Date;
	savedBy?: string;
	submittedBy?: string;
	approvedBy?: string;
	sendToJDEBy?: string;
	rolledBackBy?: string;
	isSystemRestoredTimesheet?: boolean;
	userId?: number;
}

export interface PayPeriodCdoUsageTracker {
	[id: string]: boolean;
}

export interface ReduceCdoScheduledOnStat {
	timesheets: Timesheet[];
	fromPreviousWeek: boolean;
}

export interface ScheduledActingMetadata {
	isActing: boolean;
	actingIndex: number;
}

export interface TimesheetMetadata {
	isUserAuxiliary: boolean;
	isStat: boolean;
	isCDO: boolean;
	expectedDailyHours: number;
	actingMetadata: ScheduledActingMetadata;

	isShiftedCDO?: boolean;
	dateYYYYMMDD?: string;
	statHoliday?: StatHoliday;
	bankedCDO?: BankedCDOMetadata;
}

export interface BankedCDOMetadata {
	description: string;
	date: Date;
	options: { [id: string]: any };
}

export interface TimesheetEntry2 {
	name: string;
	metadata: { [id: string]: any };
	durations: number[];
	durationsInDisplayFormat?: string[];
	batchNumber?: number;
}

export interface TimeentryDB {
	timesheetId: number;
	exceptionTypeId: number;
	entryDate: Date;
	userId?: number;
	duration: number;
	projectId?: number;
	comments: string;
	workOrderNumber?: string;
	batchNumber?: number;
	isNew: boolean;
	rate?: number;
	jobStep?: string;
	jobType?: string;
	approverId?: string;
	isScheduledActing?: boolean;
}

export interface TimeentryDBByPeriod {
	payPeriod: string;
	timeEntries: TimeentryDB[];
}

export interface PostTimesheet {
	action: string;
	employeeNumber: string;
	payPeriod: string;
	timesheetMetadata: string;
	entryData: string;
}

export interface ManagerEmployeeTimesheetView {
	employeeNumber: string;
	employeeFirstName: string;
	employeeLastName: string;
	employeeEmail: string;
	timesheets: Timesheet[];
	configurations: Configuration[];
	userId: number;
	active: boolean;
}

export interface UserTimesheet {
	userId: number;
	timekeeperId?: number;
	active: boolean;
	employeeNumber: string;
	employeeFirstName: string;
	employeeLastName: string;
	employeeEmail: string;
	configurations: Configuration[];
	timesheets: Timesheet[];
	timesheetsRolledBack?: Timesheet[];
}

export interface TimesheetApprovalSubmission {
	employeeNumber: string;
	timesheetStartDates: Date[];
	isEdit: boolean;
	isApprovingFromEditModal?: boolean;
}

export interface UsersList {
	id: number;
	fullName: string;
	isManager?: boolean;
	isTimekeeper?: boolean;
}

export interface User {
	id?: number;
	firstName: string;
	lastName: string;
	upn: string;
	email: string;
	employeeNumber: string;
	active: boolean;
	isManager: boolean;
	isTimekeeper: boolean;
	isHR: boolean;
	managerId?: number;
	createdAt?: Date;
	createdBy?: number;
	updatedAt?: Date;
	updatedBy?: number;
	timekeeperId?: number;
	enrolledGroups?: string;
	isClosePostingsIgnored?: boolean;
}

export interface ADGroupList {
	appAuthTimesheetUsers: string[];
	appAuthTimesheetManager: string[];
	appAuthTimesheetHR: string[];
	appAuthTimesheetTimekeeper: string[];
	appAuthTimesheetAdmin: string[];
}

export interface AdminUserView {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	active: boolean;
	upn: string;
}

export interface NewUser {
	firstName: string;
	lastName: string;
	upn: string;
	email: string;
	createdAt: Date;
	managerEmail: string;
	managerUPN: string;
	isManager: boolean;
	isHR: boolean;
	isTimekeeper: boolean;
	employeeNumber: string;
	active: boolean;
	managerDirectReports: ManagerAdDirectReports[];
}

export interface ManagerAdDirectReports {
	email: string;
	upn: string;
}

export interface TimesheetExceptionType {
	code: string; // The code stored to the DB
	active: boolean; // New entries of this type can be added (never remove historic codes, just make them inactive)
	entryText: string; // Text shown to the user
	colour: string; // The colour of the pill in the UI,
	group: string; // The group the time category gets organized into
}

export enum TimesheetEntryColors {
	CDO = '#009cde',
	REGULAR_DAY = '#44883e',
	SICK = '#ab037a',
	OVERTIME = '#efbe7d',
	OTHER = 'black', //'#de4200',
	STAT = '#bba600',
	// ANNUAL_LEAVE = '#fffff',
	VACATION = '#bb2200',
	STANDBY = '#aaaaaa',
}

export enum TimesheetEntryType {
	REGULAR_DAY = 'Regular',
	ACTING = 'Acting',
	OVERTIME = 'Overtime',
	COUNCIL = 'Council',
	SICK = 'Sick',
	VACATION = 'Vacation',
	CDO = 'CDO',
	BANKED = 'Banked',
	TRAINING = 'Training',
	OTHER = 'Other',
	STAT = 'Stat',
	UNION = 'Union',
	STANDBY = 'Standby',
}

export interface WorkOrderChargeAbleEntries {
	entryType: TimesheetEntryType;
	workOrderChargeable: boolean;
}

export interface TimesheetMetaDataOption {
	code: string; // The code stored to the DB
	active: boolean; // New entries of this type can be added (never remove historic codes, just make them inactive)
	entryText: string; // Text shown to the user
}

export enum BankOrPayoutOptionsCode {
	BankIt = 'BankIt',
	PayOut = 'PayOut',
}

export enum OverTimePrescheduledOptions {
	PrescheduledOTyes = 'PreSchdYes',
	PrescheduledOTno = 'PreSchdNo',
}

export enum OverTimeContextOptions {
	ScheduleAdjacent = 'AdjOT',
	ScheduledNonAdjacent = 'NonAdjOT',
}

// Paid Sick Leave (400) / Family Leave (402) / Unpaid Sick Leave (405) / WCB Sick Leave (450) / WCB No Pay Leave (Unpaid) (660)
export enum SickTypeOptions {
	Personal = 'Personal',
	Family = 'Family',
	UnpaidSick = 'UnpaidSick',
	WCBSick = 'WCBSick',
	WCBUnpaid = 'BCBUnpaid',
}

// Overtime (Current Year) / Overtime (Previous Year) / Council Overtime / CDO / Stat holiday
export enum BankedTypeOptions {
	COT = 'COT',
	POT = 'POT',
	YOT = 'YOT',
	COUNCIL_OT = 'CouncilOT',
	CDO = 'CDO', // TBD
	STAT_CDO = 'StatCDO', // TBD
}

// Compassionate Leave (605) / Jury Duty Leave (620) / Maternity Leave (Unpaid) (650) / Parental Leave (Unpaid) (652) / No Pay Leave (Unpaid Leave of absence) (655)
export enum OtherLeaveOptions {
	Compassionate = 'Compassionate',
	JuryDuty = 'JuryDuty',
	Maternity = 'Maternity',
	Parental = 'Parental',
	Unpaid = 'Unpaid',
}

export enum CDOBankingOptions {
	NextWeek = 'Next Week',
	PreviousWeek = 'Previous Week',
	HolidayClosure = 'Holiday Closure',
	StatAdjustment = 'Stat Adjustments',
	Other = 'Other',
}

// At Union's Expense (610) / At District's Expense (612)
export enum UnionBusinessOptions {
	UnionExp = 'AtUnionExp',
	DistExp = 'AtDistrictExp',
}

export interface TimeEntryDetails {
	duration: number;
	durationDisplayFormat?: string;
	status: string;
	comment: string;
	entryDate?: string | Date;
	shouldLockEntry?: boolean;
}

export interface TimeEntry {
	exceptionType: ExceptionType;
	exceptionTypeId?: number;
	projectId: number;
	entries: TimeEntryDetails[];
}

export interface PayPeriod {
	payPeriod: string;
	startDate: string | Date;
	endDate: string | Date;
	timeEntryCutOffDate: string | Date;
	payDate: string | Date;
}

export interface FormSortableColumns {
	roleSort?: boolean;
	fromUserSort?: boolean;
	toUserSort?: boolean;
	startDateSort?: boolean;
	endDateSort?: boolean;
	statusSort?: boolean;
}

export interface FormSortDirection {
	ascending: boolean;
	descending: boolean;
}

export interface ScheduledActingAssignment {
	id?: number;
	forUserId?: number;
	from: Date;
	to: Date;
	description: string;
	rate: number;
	active: boolean;
	jobType?: string;
	jobStep?: string;
}

export interface DelegationEntry {
	id?: number;
	fromUserId: number;
	toUserId: number;
	from: Date;
	to: Date;
	active?: boolean;
	createdBy?: number;
	role: string;
}

export interface AssignedDelegations {
	actingId: number;
	delegationIds: number[];
	actingForNames: string;
}

export interface LeaveRequestEntry {
	id?: number;
	fromUserId: number;
	toUserId: number;
	from: Date;
	to: Date;
	active?: boolean;
	createdBy?: number;
}

export interface PayPeriodEntry extends PayPeriod {
	timeEntry: TimeEntry[];
}

export interface DailyTimeEntryDetails {
	exceptionType: ExceptionType;
	projectId: number;
	comment: string;
	duration: number;
	status: string;
	payPeriodEntry: PayPeriodEntry;
	payPeriodIndex: number;
	timeSheetEntry?: TimeEntry;
	isPredictedData: boolean;
}

export interface SaveSubmitTimesheet {
	actionType: string;
	startDate: string;
	isShowBankCDODialog: boolean;
	timesheet: PostTimesheet;
	isEdited: boolean;
}

export interface SaveSubmitTimeEntry {
	actionType: string;
	currentWeek: string;
	isShowBankCDODialog: boolean;
	timeEntry: TimeEntry[];
}

export interface WorkWeekPredictedPattern {
	day: DaysOfTheWeek;
	predictedWorkDay: boolean;
}

export enum NotificationStates {
	Success = 'success',
	Error = 'error',
	Info = 'info',
	Warning = 'warning',
}

export interface UserNotification {
	type: NotificationStates;
	title: string;
	message: string;
	options?: { [id: string]: any };
}

export enum PayPeriodWeekNumber {
	weekOne = 1,
	weekTwo = 2,
	Unassigned = -1,
}

export interface CDOTemplate {
	day: DaysOfTheWeek;
	cdoWeek: PayPeriodWeekNumber;
}

export interface ModifiedDurationDayTemplate {
	day: DaysOfTheWeek;
	modifiedDayWeek: PayPeriodWeekNumber;
	hours: number;
}

export interface Days {
	day: RenderedDaysOfTheWeek;
	dayName: string;
}

export interface OvertimeStartDetails {
	isBeforeShift: boolean;
	isAfterShift: boolean;
	isOtherStartPoint: boolean;
	entryText: string;
}

export enum DaysOfTheWeek {
	Sunday = 0,
	Monday = 1,
	Tuesday = 2,
	Wednesday = 3,
	Thursday = 4,
	Friday = 5,
	Saturday = 6,
	Unassigned = -1,
}

export enum RenderedDaysOfTheWeek {
	Saturday = 0,
	Sunday = 1,
	Monday = 2,
	Tuesday = 3,
	Wednesday = 4,
	Thursday = 5,
	Friday = 6,
}

export interface DaysWorkedCounter {
	day: DaysOfTheWeek;
	numberTimesWorked: number;
}

export interface DailyTimeEntry {
	entries: DailyTimeEntryDetails[];
}

export interface EntryTypeTime {
	entryType: TimesheetEntryType;
	// exceptionTypeId: number;
	entryTime: number;
	exceptionType: ExceptionType;
	entryComment: string;
}

export interface CalendarDayEntry {
	isNeighborMonth: boolean;
	shouldShowDay: boolean;
	isPayDay: boolean;
	isHoliday: boolean;
	isCDO: boolean;
	isStat: boolean;
	isSick: boolean;
	isOvertime: boolean;
	statHolidayName?: string;
	isWorkingDay: boolean;
	payPeriodIndex: number;
	payPeriodInfo?: PayPeriodEntry;
	entryTypeTime: EntryTypeTime[];
	expectedDailyHours: number;
	isPredictedData: boolean;
	isSavedData: boolean;
	currentWeekInMonth: number;
	year: number;
	month: number;
	dateEntry: number;
}

export interface EntryFormTimeEntry {
	daysExceedingExpectedHours: number[];
	weekEntries: TimeEntryDetails[];
	exceptionType: ExceptionType;
	// exceptionTypeId: number;
	// exceptionTypeCode: number;
	// exceptionTypeName: string;
	isInvalidCDOEntry?: boolean;
	isDisplayedByDefault?: boolean;
	bankOrPayoutOT?: string;
	continuousOT?: string;
	sickType?: string;
	leaveType?: string;
	projectId?: number;
}

export interface EditFormChanges {
	previousEntry: EntryFormTimeEntry;
	newEntry?: EntryFormTimeEntry;
	timeEntryIndexToReplace: number;
}

export interface EditTimesheetFormChanges {
	previousEntry: TimesheetEntry2;
	newEntry?: TimesheetEntry2;
	timeEntryIndexToReplace: number;
}

export interface ExceptionType {
	id: number;
	code: number;
	name: string;
	displayName?: string;
	displayDetails?: string;
	description?: string;
	userNotification?: string;
	timeEntries?: any[] | null;
	active?: boolean;
	colour?: string;
}

export interface FilterToggleStates {
	payPeriod: boolean;
	payDay: boolean;
	statHoliday: boolean;
	holidayClosure: boolean;
	cdo: boolean;
	timesheets: boolean;
}

export interface CalendarMonthlyTimesheets {
	year: number;
	month: number;
	timesheets: Timesheet[];
	payDates: Date[];
}

export interface CalendarMonthlyEntry {
	year: number;
	month: number;
	calendarEntries: CalendarDayEntry[];
}

export interface TeamMemberProfile {
	email: string;
	profilePic: SafeUrl | null;
	status?: string;
}

export interface InitialLoadState {
	isTimesheetsLoaded: boolean;
	isPayPeriodsLoaded: boolean;
	isStatHolidaysLoaded: boolean;
	isProfileLoaded: boolean;
	isScheduledActingsLoaded: boolean;
}

export interface UserAdGroupEntry {
	upn: string;
	adGroups: string;
}

export interface UpdateMemberConfig {
	oldConfig: Configuration;
	newConfig: Configuration;
}

export interface UpdateUserAccount {
	oldAccountDetails: User;
	newAccountDetails: User;
}

export interface ApplicationState {
	payPeriodEntries: PayPeriodEntry[];
	payPeriods: PayPeriod[];
	initialLoadingState: InitialLoadState;
	calendarEntries: CalendarMonthlyEntry[];
	userScheduledActingAssignments: ScheduledActingAssignment[];
	allScheduledActingAssignments: ScheduledActingAssignment[];
	userDelegationEntries: DelegationEntry[];
	isUserCDOEnrolled: boolean;
	cdoTemplateInfo: CDOTemplate;
	regularHourDuration: number;
	modifiedDayDurationTemplate: ModifiedDurationDayTemplate;
	selectedYear: number;
	profile: Profile;
	profilePicture: Blob | string | null;
	statHolidays: StatHoliday[];
	statHolidaysMap: { [id: string]: StatHoliday };
	bankTypes: any[];
	exceptionTypes: ExceptionType[];
	exceptionTypesMap: { [id: number]: ExceptionType };
	projects: any[];
	notificationTypes: any[];
	notifications: EmailNotification[];
	updateReasons: any[];
	timeBank: TimeBank[];
	isLoadingData: boolean;
	timesheets: Timesheet[];
	timesheetHistory: Timesheet[];
	rawTimesheets: Timesheet[];
	approvedTimesheets: UserTimesheet[];
	allUserTimesheets: UserTimesheet[];
	directEmployeeTimesheets: ManagerEmployeeTimesheetView[];
	teamProfile: TeamMemberProfile[];
	timeEntries: TimeentryDB[];
	systemUser: User;
	userImages: { [id: string]: SafeUrl };
	leaveRequests: LeaveRequestEntry[];
	allUsers: User[];
}

export const initialState: ApplicationState = {
	payPeriodEntries: [],
	payPeriods: [],
	calendarEntries: [],
	userScheduledActingAssignments: [],
	allScheduledActingAssignments: [],
	userDelegationEntries: [],
	initialLoadingState: {
		isTimesheetsLoaded: false,
		isPayPeriodsLoaded: false,
		isStatHolidaysLoaded: false,
		isProfileLoaded: false,
		isScheduledActingsLoaded: false,
	},

	// Testing reducer code for the calender object generation
	isUserCDOEnrolled: false,
	cdoTemplateInfo: {
		day: DaysOfTheWeek.Unassigned,
		cdoWeek: PayPeriodWeekNumber.Unassigned,
	},
	regularHourDuration: 7,
	modifiedDayDurationTemplate: {
		day: DaysOfTheWeek.Unassigned,
		modifiedDayWeek: PayPeriodWeekNumber.Unassigned,
		hours: 0,
	},

	selectedYear: new Date().getFullYear(),
	profile: {
		configuration: [],
		manage: [],
		managedGoneFromDNV: [],
		timekeeperDelegated: [],
		timekeeperManagedUsers: [],
		allUsers: [],
		delegatedFrom: [],
		delegatedTo: [],
		delegated: [],
		scheduledActings: [],
		executiveAssistantFor: [],
		user: {
			id: -1,
			firstName: '',
			lastName: '',
			upn: '',
			email: '',
			active: false,
			employeeNumber: '',
			isManager: false,
			isTimekeeper: false,
			isHR: false,
			managerId: 0,
			createdAt: new Date(),
			createdBy: 0,
			updatedAt: new Date(),
			updatedBy: 0,
		},
	},
	profilePicture: null,
	statHolidays: [],
	statHolidaysMap: {},
	bankTypes: [],
	exceptionTypes: [],
	exceptionTypesMap: {},
	projects: [],
	notificationTypes: [],
	notifications: [],
	updateReasons: [],
	timeBank: [],
	isLoadingData: true,
	timesheets: [],
	timesheetHistory: [],
	rawTimesheets: [],
	approvedTimesheets: [],
	allUserTimesheets: [],
	directEmployeeTimesheets: [],
	teamProfile: [],
	timeEntries: [],
	systemUser: {
		id: -1,
		firstName: '',
		lastName: '',
		upn: '',
		email: '',
		active: false,
		employeeNumber: '',
		isManager: false,
		isTimekeeper: false,
		isHR: false,
		managerId: 0,
		createdAt: new Date(),
		createdBy: 0,
		updatedAt: new Date(),
		updatedBy: 0,
	},
	userImages: {
		'AutomatedTesting1@dnv.org': '/assets/images/AutomatedTesting1.jpg',
		'AutomatedTesting2@dnv.org': '/assets/images/AutomatedTesting2.jpg',
		'AutomatedTesting3@dnv.org': '/assets/images/AutomatedTesting3.jpg',
		'timesheet@dnv.org': '/assets/images/SystemAccount.png',
	},
	leaveRequests: [],
	allUsers: [],
};

export interface PostEntries {
	action: string;
	timeEntries: any;
}

export enum TimeEntryStatus {
	Saved = '',
	Submitted = 'Submitted',
	Approved = 'Approved',
	SenttoJDE = 'Sent to JDE',
}

export enum TimesheetStatus {
	Predicted = 'Predicted',
	Saved = 'Saved',
	Submitted = 'Submitted',
	Approved = 'Approved',
	Deleted = 'Deleted',
	JDE = 'JDE',
	RolledBack = 'Rolled Back',
}

export enum BankType {
	CDO = 1,
	Vacation = 2,
	Sick = 3,
	OvertimePreviousYear = 4,
	OvertimeCurrentYear = 5,
	StatutoryHolidays = 6,
	FamilyLeave = 7,
	CouncilOT = 8,
}

export enum EmploymentStatus {
	RFT = 'RFT',
	RPT = 'RPT',
	TFT = 'TFT',
	AUX = 'AUX',
}

export enum UnionCode {
	Union = 'Union',
	ExemptSupport = 'ExemptSupport',
	ExemptManagement = 'ExemptManagement',
}
